import { useI18n } from "@/i18n/I18nProvider";
import { Reveal } from "@/hooks/useReveal";
import {
  Banknote,
  Building2,
  Truck,
  HeartPulse,
  GraduationCap,
  ShoppingBag,
  Landmark,
  Rocket,
  type LucideIcon,
} from "lucide-react";

const items: { key: string; Icon: LucideIcon }[] = [
  { key: "fintech",     Icon: Banknote },
  { key: "realestate",  Icon: Building2 },
  { key: "logistics",   Icon: Truck },
  { key: "healthcare",  Icon: HeartPulse },
  { key: "education",   Icon: GraduationCap },
  { key: "retail",      Icon: ShoppingBag },
  { key: "gov",         Icon: Landmark },
  { key: "startups",    Icon: Rocket },
];

export function IndustriesSection({ index = "02" }: { index?: string }) {
  const { t } = useI18n();
  const data = t.industries;

  return (
    <section className="border-t hairline relative overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <Reveal className="lg:col-span-6">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-4">
              — {index} / {data.eyebrow}
            </p>
            <h2 className="font-display text-4xl lg:text-6xl font-semibold tracking-tighter">
              {data.title}
            </h2>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-5 lg:col-start-8 lg:pt-4">
            <p className="text-lg text-muted-foreground leading-relaxed text-pretty">
              {data.subtitle}
            </p>
          </Reveal>
        </div>

        <div className="grid gap-px bg-border border hairline rounded-2xl overflow-hidden sm:grid-cols-2 lg:grid-cols-4">
          {items.map(({ key, Icon }, i) => {
            const item = (data.items as Record<string, { t: string; d: string }>)[key];
            return (
              <Reveal key={key} delay={i * 50} className="bg-background">
                <div className="group relative h-full p-6 lg:p-8 card-lift cursor-default">
                  <div className="flex items-start justify-between mb-8">
                    <Icon
                      className="h-6 w-6 text-foreground/80 transition-transform duration-500 group-hover:scale-110"
                      strokeWidth={1.4}
                    />
                    <span className="text-[10px] font-mono text-muted-foreground/60">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="font-display text-lg lg:text-xl font-semibold tracking-tight mb-2">
                    {item.t}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}